import React, { useEffect, useState } from 'react';
import { Modal, Table, Checkbox, Tag, message } from 'antd';
import { apiRequest } from '../../../service/apiRequest';  // your API util
import { showSuccess, showError } from '../../../components/errorHandler';


interface SessionItem {
  session_id: number;
  session_name: string;
  session_status: number;
  som_id: string | null; // null means NOT mapped
}

interface Props {
  visible: boolean;
  onCancel: () => void;
  sessionData: SessionItem[];
  organizationId: string; // pass org id to link session
  onSaved: () => void; // callback after successful save
}

const SessionOrgMapping: React.FC<Props> = ({
  visible,
  onCancel,
  sessionData,
  organizationId,
  onSaved,
}) => {
  const [selectedSessionIds, setSelectedSessionIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const mappedIds = sessionData
      .filter((cls) => cls.som_id !== null)
      .map((cls) => cls.session_id);
    setSelectedSessionIds(mappedIds);
  }, [sessionData]);

  const onCheckChange = (SessionId: number, checked: boolean) => {
    setSelectedSessionIds((prev) =>
      checked ? [...prev, SessionId] : prev.filter((id) => id !== SessionId)
    );
  };

  const columns = [
    {
      title: 'Session Name',
      dataIndex: 'session_name',
      key: 'session_name',
    },
    {
      title: 'Status',
      dataIndex: 'session_status',
      key: 'session_status',
      render: (status: number) =>
        status === 1 ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: 'Mapped',
      dataIndex: 'som_id',
      key: 'mapped',
      render: (som_id: string | null) =>
        som_id ? (
          <Tag color="blue">Mapped</Tag>
        ) : (
          <Tag color="default">Not Mapped</Tag>
        ),
    },
    {
      title: 'Select',
      key: 'select',
      render: (_: any, record: SessionItem) => (
        <Checkbox
          checked={selectedSessionIds.includes(record.session_id)}
          onChange={(e) => onCheckChange(record.session_id, e.target.checked)}
        />
      ),
    },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      // API call - replace URL & payload as needed
      await apiRequest({
        method: 'put',
        url: `/map-session-to-organization/${organizationId}`, // example endpoint
        data: { session_ids: selectedSessionIds },
      });
      showSuccess('Session mapped successfully');
      onSaved(); // notify parent to refresh or close modal
      onCancel();
    } catch (error) {
      console.error(error);
      showError('Failed to save session mappings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Map Session to Organization"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText="Save"
      confirmLoading={loading}
      width={600}
    >
      <Table
        rowKey="session_id"
        dataSource={sessionData}
        columns={columns}
        pagination={false}
        scroll={{ y: 300 }}
      />
    </Modal>
  );
};

export default SessionOrgMapping;
